import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import OTPInput from '@/components/OTPInput';
import CountdownTimer from '@/components/CountdownTimer';
import { getPostLoginRedirect, GuestIntent, hadPriorCustomerSession } from '../../utils/storage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  const userName = location.state?.name || 'User';

  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resending, setResending] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(300); // 5 minutes

  useEffect(() => {
    // Redirect if no email provided
    if (!email) {
      navigate('/auth');
      return;
    }

    // Start 5-minute cooldown for resend
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [email, navigate]);

  const formatCooldown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 4) {
      setError('Please enter the complete 4-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp })
      });

      // Try to parse JSON response
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (jsonError) {
          console.error('JSON parse error:', jsonError);
          throw new Error('Invalid server response');
        }
      } else {
        throw new Error('Server returned non-JSON response');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }

      // Server already set the httpOnly session cookies on verify-email
      // success — cache only the non-sensitive display object.
      // SECURITY: cross-user leak guard — if a different user was already
      // signed in on this device, their leftover GuestIntent pick must not
      // carry over to this freshly-verified account.
      if (hadPriorCustomerSession()) {
        GuestIntent.clear();
      }
      localStorage.setItem('user', JSON.stringify(data.user));

      setSuccess('Email verified! Redirecting...');
      
      // Force a full reload so AuthProvider re-initialises from the freshly
      // written token + user. A plain navigate('/home') keeps the in-memory
      // AuthContext state stale (user === null) and HomePage bounces back
      // to '/'.
      setTimeout(() => {
        window.location.href = getPostLoginRedirect();
      }, 1500);

    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message || 'An error occurred');
      setOtp(''); // Clear OTP on error
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || resending) return;

    setResending(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      // Try to parse JSON response
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (jsonError) {
          console.error('JSON parse error:', jsonError);
          throw new Error('Invalid server response');
        }
      } else {
        throw new Error('Server returned non-JSON response');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to resend code');
      }

      setSuccess('New verification code sent!');
      setCanResend(false);
      setResendCooldown(300); // Reset cooldown to 5 minutes
      setOtp(''); // Clear current OTP

    } catch (err) {
      console.error('Resend error:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setResending(false);
    }
  };

  const handleOTPExpire = () => {
    setError('Verification code expired. Please request a new one.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-black font-heading uppercase text-emerald-700 mb-2">
            Check Your Email
          </h1>
          <p className="text-gray-600">
            We sent a verification code to
          </p>
          <p className="text-sm font-semibold text-emerald-700 mt-1">
            {email}
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-emerald-50 border-l-4 border-emerald-500 text-emerald-700 rounded">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">{success}</span>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">{error}</span>
            </div>
          </div>
        )}

        {/* OTP Form */}
        <form onSubmit={handleVerify} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-4 text-center">
              Enter 4-Digit Code
            </label>
            <OTPInput
              length={4}
              value={otp}
              onChange={setOtp}
              disabled={loading || !!success}
            />
          </div>

          {/* Countdown Timer */}
          <div className="flex justify-center">
            <CountdownTimer
              initialSeconds={900}
              onExpire={handleOTPExpire}
            />
          </div>

          {/* Verify Button */}
          <Button
            type="submit"
            disabled={loading || otp.length !== 4 || !!success}
            className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white font-bold py-3 rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Verifying...</span>
              </div>
            ) : (
              'Verify Email'
            )}
          </Button>

          {/* Resend Section */}
          <div className="text-center space-y-2">
            <p className="text-sm text-gray-600">
              Didn't receive the code?
            </p>
            {canResend ? (
              <Button
                type="button"
                onClick={handleResend}
                disabled={resending}
                variant="ghost"
                className="text-emerald-600 hover:text-emerald-700 font-semibold"
              >
                {resending ? 'Sending...' : 'Resend Code'}
              </Button>
            ) : (
              <p className="text-sm text-gray-500">
                Resend available in <span className="font-semibold text-emerald-600">{formatCooldown(resendCooldown)}</span>
              </p>
            )}
          </div>

          {/* Help Text */}
          <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
            <p className="text-sm text-yellow-800">
              <strong>💡 Tip:</strong> Check your spam folder if you don't see the email.
            </p>
          </div>

          {/* Back to Login */}
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/auth')}
              className="text-sm text-gray-600 hover:text-emerald-600 font-semibold"
            >
              ← Back to Login
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
